import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { fetchDashboardStats, fetchStatsPerRegione } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Users, FolderKanban, CheckCircle, Clock, MapPin, User, Phone, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [regionStats, setRegionStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, isSegretario, isAdminOrdine } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const dashboardData = await fetchDashboardStats();
      setStats(dashboardData);
      
      // Only load region stats for full dashboard
      if (dashboardData.tipo === 'full') {
        const regionData = await fetchStatsPerRegione();
        setRegionStats(regionData);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2].map(i => (
              <div key={i} className="h-48 bg-slate-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Dashboard per Admin Ordine - mostra solo il proprio ordine
  if (stats?.tipo === 'admin_ordine') {
    return <AdminOrdineDashboard stats={stats} user={user} navigate={navigate} />;
  }

  // Coordinatore GTL - redirect directly to their GTL detail page
  if (stats?.tipo === 'coordinatore_gtl') {
    // Redirect to GTL detail page
    if (user?.gtl_id) {
      navigate(`/gtl/${user.gtl_id}`, { replace: true });
      return (
        <div className="p-6 lg:p-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-500">Reindirizzamento al tuo GTL...</div>
          </div>
        </div>
      );
    }
    // Fallback if no GTL assigned
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Nessun GTL assegnato</div>
        </div>
      </div>
    );
  }

  // Dashboard completa per Superadmin e Osservatore
  return <FullDashboard stats={stats} regionStats={regionStats} user={user} />;
}

// Dashboard per Admin Ordine
function AdminOrdineDashboard({ stats, user, navigate }) {
  const { ordine, delega, delegato, supplente } = stats;
  const [membriGTL, setMembriGTL] = useState([]);
  const [loadingMembri, setLoadingMembri] = useState(true);

  useEffect(() => {
    const fetchMembriGTL = async () => {
      try {
        const token = localStorage.getItem('c3i_token');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ordine/membri-gtl?Authorization=${encodeURIComponent(`Bearer ${token}`)}`
        );
        if (response.ok) {
          const data = await response.json();
          setMembriGTL(data);
        }
      } catch (error) {
        console.error('Error fetching membri GTL:', error);
      } finally {
        setLoadingMembri(false);
      }
    };
    fetchMembriGTL();
  }, []);

  // Format date from YYYY-MM-DD or ISO to DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard-admin-ordine">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Dashboard</h1>
      </div>

      {/* Ordine Info */}
      <Card className="card-institutional mb-6" data-testid="ordine-info-card">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-slate-100 rounded-lg">
              <Building2 className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">{ordine?.denominazione}</CardTitle>
              <p className="text-sm text-slate-500">{ordine?.citta} ({ordine?.sigla_provincia}) - {ordine?.regione}</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Delegato e Supplente */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delegato */}
        <Card className="card-institutional" data-testid="delegato-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-slate-600" />
                Delegato C3i
              </CardTitle>
              {delegato ? (
                <span className="badge-success">Nominato</span>
              ) : (
                <span className="badge-warning">Da nominare</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {delegato ? (
              <div className="space-y-3">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-semibold text-slate-900 text-lg">
                    {delegato.nome} {delegato.cognome}
                  </p>
                </div>
                {delegato.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${delegato.email}`} className="hover:text-slate-900">{delegato.email}</a>
                  </div>
                )}
                {delegato.cellulare && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${delegato.cellulare}`} className="hover:text-slate-900">{delegato.cellulare}</a>
                  </div>
                )}
                {delega?.data_inizio && (
                  <p className="text-xs text-slate-400 mt-2">Nominato dal: {formatDate(delega.data_inizio)}</p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <User className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">Nessun delegato nominato</p>
                <button 
                  onClick={() => navigate('/nomina-delegati?tipo=DELEGATO')}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                  data-testid="link-nomina-delegato"
                >
                  Nomina Delegato
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Supplente */}
        <Card className="card-institutional" data-testid="supplente-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-600" />
                Delegato Supplente
              </CardTitle>
              {supplente ? (
                <span className="badge-success">Nominato</span>
              ) : (
                <span className="badge-neutral">Opzionale</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {supplente ? (
              <div className="space-y-3">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-semibold text-slate-900 text-lg">
                    {supplente.nome} {supplente.cognome}
                  </p>
                </div>
                {supplente.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${supplente.email}`} className="hover:text-slate-900">{supplente.email}</a>
                  </div>
                )}
                {supplente.cellulare && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${supplente.cellulare}`} className="hover:text-slate-900">{supplente.cellulare}</a>
                  </div>
                )}
                {delega?.updated_at && (
                  <p className="text-xs text-slate-400 mt-2">Nominato dal: {formatDate(delega.updated_at)}</p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">Nessun supplente nominato</p>
                <button 
                  onClick={() => navigate('/nomina-delegati?tipo=SUPPLENTE')}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                  data-testid="link-nomina-supplente"
                >
                  Nomina Supplente
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Membri GTL dell'Ordine */}
      <Card className="card-institutional mt-6" data-testid="membri-gtl-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-slate-600" />
              Iscritti dell'Ordine nei GTL
            </CardTitle>
            <span className="text-sm text-slate-500">{membriGTL.length} iscritti</span>
          </div>
        </CardHeader>
        <CardContent>
          {loadingMembri ? (
            <div className="flex justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : membriGTL.length === 0 ? (
            <div className="text-center py-8">
              <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun iscritto dell'Ordine presente nei GTL</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="membri-gtl-table">
                <thead>
                  <tr>
                    <th>Nome e Cognome</th>
                    <th>Email</th>
                    <th>Cellulare</th>
                    <th>Ruolo GTL</th>
                  </tr>
                </thead>
                <tbody>
                  {membriGTL.map((membro) => (
                    <tr key={membro.id}>
                      <td>
                        <span className="font-medium">{membro.nome} {membro.cognome}</span>
                      </td>
                      <td>
                        {membro.email ? (
                          <a href={`mailto:${membro.email}`} className="text-blue-600 hover:underline">
                            {membro.email}
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td>
                        {membro.cellulare ? (
                          <a href={`tel:${membro.cellulare}`} className="hover:text-slate-900">
                            {membro.cellulare}
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td>
                        <div>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            membro.ruolo_gtl === 'Coordinatore' 
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : membro.ruolo_gtl === 'Referente'
                              ? 'bg-blue-100 text-blue-700 border border-blue-200'
                              : 'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}>
                            {membro.ruolo_gtl}
                          </span>
                          <p className="text-xs text-slate-500 mt-1">{membro.gtl_nome}</p>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Dashboard per Coordinatore GTL
function CoordinatoreGTLDashboard({ stats, user, navigate }) {
  const { gtl, membri, total_membri } = stats;

  const getRuoloBadge = (ruolo) => {
    return ruolo === 'coordinatore' 
      ? <span className="badge-success">Coordinatore</span>
      : <span className="badge-neutral">Membro</span>;
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'attivo':
        return <span className="badge-success">Attivo</span>;
      case 'concluso':
        return <span className="badge-neutral">Concluso</span>;
      case 'sospeso':
        return <span className="badge-warning">Sospeso</span>;
      default:
        return <span className="badge-neutral">{stato}</span>;
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard-coordinatore-gtl">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Il Mio GTL</h1>
        <p className="text-slate-500 mt-1">
          Benvenuto, {user?.nome} {user?.cognome}
        </p>
      </div>

      {/* GTL Info */}
      <Card className="card-institutional mb-6" data-testid="gtl-info-card">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FolderKanban className="w-6 h-6 text-blue-700" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <CardTitle className="font-heading text-lg">{gtl?.nome}</CardTitle>
                {getStatoBadge(gtl?.stato)}
              </div>
              <p className="text-sm text-slate-500 mt-1">{gtl?.tema}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {gtl?.descrizione && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-700">{gtl.descrizione}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {gtl?.data_inizio && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Inizio: {gtl.data_inizio}</span>
              </div>
            )}
            {gtl?.data_fine && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Fine: {gtl.data_fine}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Users className="w-4 h-4" />
              <span>{total_membri} membri</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members List */}
      <Card className="card-institutional">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Membri del Gruppo</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {membri?.length === 0 ? (
            <div className="empty-state py-12">
              <Users className="empty-state-icon" />
              <p className="empty-state-title">Nessun membro</p>
              <p className="empty-state-description">Questo GTL non ha ancora membri</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table" data-testid="membri-table">
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th>Ruolo</th>
                    <th>Contatti</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {membri?.map((membro) => (
                    <tr key={membro.id}>
                      <td>
                        <div>
                          <p className="font-medium text-slate-900">
                            {membro.persona?.titolo} {membro.persona?.nome} {membro.persona?.cognome}
                          </p>
                          {membro.persona?.email && (
                            <p className="text-xs text-slate-500">{membro.persona.email}</p>
                          )}
                        </div>
                      </td>
                      <td>{getRuoloBadge(membro.ruolo_nel_gtl)}</td>
                      <td className="text-sm">{membro.contatti || membro.persona?.cellulare || '-'}</td>
                      <td className="text-sm max-w-xs truncate">{membro.note || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Dashboard completa per Superadmin/Lettore
function FullDashboard({ stats, regionStats, user }) {
  const statCards = [
    {
      title: 'Ordini Territoriali',
      value: stats?.total_ordini || 0,
      icon: Building2,
      color: 'bg-slate-100 text-slate-700',
      description: 'Numero totale degli ordini'
    },
    {
      title: 'Ordini con delegato',
      value: stats?.ordini_con_delegato || 0,
      icon: CheckCircle,
      color: 'bg-emerald-100 text-emerald-700',
      description: 'Ordini con Delegato C3i nominato'
    },
    {
      title: 'GTL attivi',
      value: stats?.gtl_attivi || 0,
      icon: FolderKanban,
      color: 'bg-blue-100 text-blue-700',
      description: 'GTL attualmente attivi'
    },
  ];

  const completionRate = stats?.total_ordini > 0 
    ? Math.round((stats.ordini_con_delegato / stats.total_ordini) * 100) 
    : 0;

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">
          Benvenuto, {user?.nome} {user?.cognome}
        </p>
      </div>

      {/* Stats Grid - 3 boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} className="card-institutional" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">{stat.title}</p>
                  <p className="stat-value">{stat.value}</p>
                  <p className="text-xs text-slate-400 mt-2">{stat.description}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Completion Card + Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="card-institutional" data-testid="completion-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Completamento Deleghe</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Ordini con delegato assegnato</span>
                <span className="font-semibold text-slate-900">{completionRate}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div 
                  className="bg-slate-900 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${completionRate}%` }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-2xl font-bold text-emerald-700">{stats?.ordini_con_delegato || 0}</p>
                  <p className="text-xs text-emerald-600">Con delegato</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-2xl font-bold text-slate-700">{stats?.ordini_senza_delegato || 0}</p>
                  <p className="text-xs text-slate-500">Senza delegato</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-institutional" data-testid="overview-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Panoramica Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-slate-600" />
                  <span className="text-sm text-slate-600">Utenti totali nel sistema</span>
                </div>
                <span className="font-semibold text-slate-900">{stats?.utenti_totali || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-slate-600" />
                  <span className="text-sm text-slate-600">Delegati e supplenti</span>
                </div>
                <span className="font-semibold text-slate-900">{stats?.delegati_supplenti || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FolderKanban className="w-5 h-5 text-slate-600" />
                  <span className="text-sm text-slate-600">Componenti GTL</span>
                </div>
                <span className="font-semibold text-slate-900">{stats?.componenti_gtl || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Regional Stats */}
      <Card className="card-institutional" data-testid="region-stats-card">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-600" />
            <CardTitle className="font-heading text-lg">Statistiche per Regione</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {regionStats.map((region) => (
              <div 
                key={region.regione} 
                className="p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
                data-testid={`region-stat-${region.regione}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-900 truncate">{region.regione}</span>
                  <span className="text-xs font-semibold text-slate-500">{region.totale}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 mb-1">
                  <div 
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      region.percentuale === 100 ? 'bg-emerald-500' : 
                      region.percentuale > 50 ? 'bg-blue-500' : 
                      region.percentuale > 0 ? 'bg-amber-500' : 'bg-slate-300'
                    }`}
                    style={{ width: `${region.percentuale}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={`${region.con_delegato > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {region.con_delegato} completi
                  </span>
                  <span className={`font-medium ${
                    region.percentuale === 100 ? 'text-emerald-600' : 
                    region.percentuale > 50 ? 'text-blue-600' : 'text-slate-500'
                  }`}>
                    {Math.round(region.percentuale)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
